import { useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { PageTitle } from '~/components/common';
import {
  CollectingResultReportFilter,
  CollectingResultReportSummaryBoard,
  CollectingResultReportTable,
  IN_PROGRESS_STATUSES,
  type TypeCollectingResultReportSummaryValueKey,
} from '~/components/delivery-agency';
import { DeliveryAgencyExcelDownloadModal } from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useModal, useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetCollectingResultReportsQuery,
  useGetCollectingResultReportsSearchConditionsQuery,
  usePostCollectingResultReportsDownloadMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { OPTION_ALL, TOAST_MESSAGE } from '~/constants';
import { BREAD_CRUMB } from '~/constants/breadcrumb';
import {
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
} from '~/constants/delivery-agency';
import { PATH } from '~/constants/path';

import type {
  CollectingResultReportFilterOptions,
  CollectingResultReportFilterValues,
  CollectingResultReportFilterValuesStatus,
  CollectingResultReportsDownloadInput,
  GetCollectingResultReportsParams,
} from '~/types/delivery-agency.types';

const EMPTY_FILTER_OPTIONS: CollectingResultReportFilterOptions = {
  searchKeywordTypes: [],
  collectingShippingLabelStatuses: [],
  collectingFailureReasonCategories: [],
};

type TypeSummarySelection = {
  valueKeys: TypeCollectingResultReportSummaryValueKey[];
};

const INITIAL_SUMMARY_SELECTION: TypeSummarySelection = { valueKeys: [] };

const getDefaultCollectingRequestedDateRange = () => ({
  start: dayjs().startOf('day').toDate(),
  end: dayjs().startOf('day').toDate(),
});

const INITIAL_SEARCH_FILTER_VALUES: CollectingResultReportFilterValues = {
  keywordType: null,
  keyword: '',
  collectingRequestedDateRange: getDefaultCollectingRequestedDateRange(),
  collectingShippingLabelStatuses: [OPTION_ALL],
  collectingFailureReasonCategories: [OPTION_ALL],
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
    totalCount: 0,
  },
};

const INITIAL_SEARCH_FILTER_VALUES_STATUS: CollectingResultReportFilterValuesStatus = {
  keyword: 'default',
  collectingRequestedDateRange: 'default',
};

const selectedCodes = (options: TypeSelectOption<string>[]) =>
  options.filter((option) => option.value !== OPTION_ALL.value).map((option) => option.value);

/** 서머리 선택을 서버가 받는 상태 코드 배열로 편다. `진행 중`은 하위 상태들로 치환된다 */
const toSummaryStatuses = (selection: TypeSummarySelection): string[] => {
  if (selection.valueKeys.includes('totalCount')) return [];

  return selection.valueKeys.flatMap<string>((key) => (key === 'IN_PROGRESS' ? IN_PROGRESS_STATUSES : [key]));
};

const DeliveryAgencyCollectingReportPage = () => {
  const queryClient = useQueryClient();
  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const [searchFilterValues, setSearchFilterValues] = useState(INITIAL_SEARCH_FILTER_VALUES);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState(INITIAL_SEARCH_FILTER_VALUES_STATUS);
  const [reportsQueryParams, setReportsQueryParams] = useState<GetCollectingResultReportsParams | null>(null);
  const [summarySelection, setSummarySelection] = useState<TypeSummarySelection>(INITIAL_SUMMARY_SELECTION);

  const searchConditionsQuery = useGetCollectingResultReportsSearchConditionsQuery({ showGlobalErrorToast: false });
  const reportsQuery = useGetCollectingResultReportsQuery({ params: reportsQueryParams, showGlobalDimOverlay: false });
  const reportsDownloadMutation = usePostCollectingResultReportsDownloadMutation();

  const excelDownloadModal = useModal();

  const filterOptions = searchConditionsQuery.data ?? EMPTY_FILTER_OPTIONS;
  // 키워드 구분은 고르기 전까지 검색조건의 첫 옵션(반품요청번호)을 기본값으로 쓴다
  const resolvedFilterValues = {
    ...searchFilterValues,
    keywordType: searchFilterValues.keywordType ?? filterOptions.searchKeywordTypes[0] ?? null,
  };
  const items = reportsQuery.data?.data.items ?? null;
  const summary = reportsQuery.data?.data.summary ?? null;

  const pagination = {
    currentPage: reportsQuery.data?.pagination.cursor ?? searchFilterValues.pagination.currentPage,
    pageSize: reportsQuery.data?.pagination.listView ?? searchFilterValues.pagination.pageSize,
    totalCount: reportsQuery.data?.pagination.listTotal ?? 0,
  };

  const buildListParams = (
    values: CollectingResultReportFilterValues,
    selection: TypeSummarySelection
  ): GetCollectingResultReportsParams | null => {
    if (!values.collectingRequestedDateRange) return null;

    const { start, end } = values.collectingRequestedDateRange;
    const statuses = selectedCodes(values.collectingShippingLabelStatuses);
    const failureReasons = selectedCodes(values.collectingFailureReasonCategories);
    const summaryStatuses = toSummaryStatuses(selection);
    const hasKeyword = Boolean(values.keywordType?.value && values.keyword);

    return {
      collectingRequestedDateFrom: formatDate(start, 'YYYY-MM-DD') ?? '',
      collectingRequestedDateTo: formatDate(end, 'YYYY-MM-DD') ?? '',
      ...addIf(hasKeyword, 'searchKeywordType', values.keywordType?.value),
      ...addIf(hasKeyword, 'searchKeyword', values.keyword),
      ...addIf(statuses.length > 0, 'collectingShippingLabelStatuses', statuses.join(',')),
      ...addIf(failureReasons.length > 0, 'collectingFailureReasonCategories', failureReasons.join(',')),
      ...addIf(summaryStatuses.length > 0, 'summaryStatuses', summaryStatuses.join(',')),
      page: values.pagination.currentPage,
      size: values.pagination.pageSize,
    };
  };

  /** 목록 조회 params를 다운로드 input으로 변환한다. 다중 값은 배열이고 페이지 정보는 보내지 않는다 */
  const toDownloadInput = (params: GetCollectingResultReportsParams): CollectingResultReportsDownloadInput => ({
    collectingRequestedDateFrom: params.collectingRequestedDateFrom,
    collectingRequestedDateTo: params.collectingRequestedDateTo,
    ...addIf(params.searchKeywordType, 'searchKeywordType', params.searchKeywordType),
    ...addIf(params.searchKeyword, 'searchKeyword', params.searchKeyword),
    ...addIf(
      params.collectingShippingLabelStatuses,
      'collectingShippingLabelStatuses',
      params.collectingShippingLabelStatuses?.split(',')
    ),
    ...addIf(
      params.collectingFailureReasonCategories,
      'collectingFailureReasonCategories',
      params.collectingFailureReasonCategories?.split(',')
    ),
    ...addIf(params.summaryStatuses, 'summaryStatuses', params.summaryStatuses?.split(',')),
  });

  const handleConfirmExcelDownload = (reason: string) => {
    if (!reportsQueryParams) return;

    reportsDownloadMutation.mutate(
      { downloadReason: reason, input: toDownloadInput(reportsQueryParams) },
      {
        onSuccess: () => {
          excelDownloadModal.handleConfirmModal();
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const applyParams = (params: GetCollectingResultReportsParams) => {
    queryClient.invalidateQueries({ queryKey: deliveryAgencyKeys.collectingResultReports(params) });
    setReportsQueryParams(params);
  };

  const handleSearch = (
    values: CollectingResultReportFilterValues,
    selection: TypeSummarySelection = summarySelection
  ) => {
    if (!values.collectingRequestedDateRange) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, collectingRequestedDateRange: 'error' }));
      dialogService.addDialog({ type: 'alert', children: '반품접수일을 입력 후 검색해 주세요.' });
      return;
    }

    const { start, end } = values.collectingRequestedDateRange;
    if (dayjs(end).diff(dayjs(start), 'day') + 1 > MAX_DELIVERY_AGENCY_DATE_RANGE) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, collectingRequestedDateRange: 'error' }));
      dialogService.addDialog({ type: 'alert', children: '최대 31일로 입력 후 검색해주세요.' });
      return;
    }

    const params = buildListParams(values, selection);
    if (!params) return;

    setSearchFilterValues(values);
    setSearchFilterValuesStatus(INITIAL_SEARCH_FILTER_VALUES_STATUS);
    setSummarySelection(selection);
    applyParams(params);
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      collectingRequestedDateRange: getDefaultCollectingRequestedDateRange(),
    });
    setSearchFilterValuesStatus(INITIAL_SEARCH_FILTER_VALUES_STATUS);
  };

  const searchFromFirstPage = (selection: TypeSummarySelection = summarySelection) =>
    handleSearch(
      {
        ...resolvedFilterValues,
        pagination: { ...resolvedFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
      },
      selection
    );

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    if (!reportsQueryParams) return;

    setSearchFilterValues((prev) => ({ ...prev, pagination: { ...prev.pagination, pageSize: value.value } }));
    applyParams({ ...reportsQueryParams, page: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR, size: value.value });
  };

  const handleChangePage = (page: number) => {
    if (!reportsQueryParams) return;

    applyParams({ ...reportsQueryParams, page });
  };

  const handleSelectSummary = (valueKey: TypeCollectingResultReportSummaryValueKey) => {
    if (!reportsQueryParams) return;

    const nextSelection: TypeSummarySelection =
      valueKey === 'totalCount'
        ? { valueKeys: ['totalCount'] }
        : {
            valueKeys: summarySelection.valueKeys.includes(valueKey)
              ? summarySelection.valueKeys.filter((key) => key !== valueKey)
              : [...summarySelection.valueKeys.filter((key) => key !== 'totalCount'), valueKey],
          };

    searchFromFirstPage(nextSelection);
  };

  return (
    <PageWrapper>
      <PageTitle
        title="회수 결과 리포트"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_COLLECTING_REPORT]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <CollectingResultReportFilter
          options={filterOptions}
          values={resolvedFilterValues}
          valuesStatus={searchFilterValuesStatus}
          onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
          onSearch={() => searchFromFirstPage(INITIAL_SUMMARY_SELECTION)}
          onReset={handleResetFilterValues}
        />
      )}
      <Divider color="gray100" />
      <CollectingResultReportSummaryBoard
        summary={summary}
        selectedValueKeys={summarySelection.valueKeys}
        onSelect={handleSelectSummary}
      />
      <CollectingResultReportTable
        data={items}
        pagination={pagination}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onClickExcelDownload={excelDownloadModal.handleOpenModal}
      />
      {excelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          title="리포트 전체 다운로드"
          open={excelDownloadModal.isOpen}
          onCancel={excelDownloadModal.handleCloseModal}
          onConfirm={handleConfirmExcelDownload}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyCollectingReportPage;
